<template>
  <div>
    <SetupPage
        page-label="Subjects"
        eyebrow="CURRICULUM"
        title="Subjects"
        description="Assign the subjects offered for each class and option so they appear on timetables, marks and report cards."
        toolbar-title="Assigned subjects"
        :meta="scopeLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="selectedOptionId && schsubjectitems !== null" class="ws-stat">
                <strong>{{ subjects.length }}</strong>
                <span>{{ subjects.length === 1 ? "subject" : "subjects" }}</span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                :disabled="!classes.length || isSaving"
                :title="classes.length ? 'Assign a subject to a class option' : 'Create a class first'"
                @click="openAssign"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Assign</span>
            </button>
        </template>

        <template slot="toolbar">
            <label class="ws-scope">
                <span>Class</span>
                <select
                    v-model="selectedClassId"
                    :disabled="classesLoading || !classes.length"
                    aria-label="Class to show assigned subjects for"
                    @change="onClassChange"
                >
                    <option v-if="classesLoading" :value="0" disabled>Loading classes…</option>
                    <option v-else :value="0">Select class</option>
                    <option v-for="item in classes" :key="item.Id" :value="item.Id">
                        {{ item.Sch_Class }}
                    </option>
                </select>
            </label>

            <label class="ws-scope">
                <span>Option</span>
                <select
                    v-model="selectedOptionId"
                    :disabled="!selectedClassId || optionsLoading || !options.length"
                    aria-label="Option to show assigned subjects for"
                    @change="onOptionChange"
                >
                    <option v-if="!selectedClassId" :value="0" disabled>Select class first</option>
                    <option v-else-if="optionsLoading" :value="0" disabled>Loading options…</option>
                    <option v-else-if="!options.length" :value="0" disabled>No options</option>
                    <option v-else :value="0">Select option</option>
                    <option v-for="item in options" :key="item.Id" :value="item.Id">
                        {{ item.ClassOption }}
                    </option>
                </select>
            </label>

            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search subjects"
                    aria-label="Search assigned subjects"
                    :disabled="!subjects.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="classesLoading" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading classes</strong>
            <span>Fetching the classes available at your school.</span>
        </div>

        <div v-else-if="classesFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Classes could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryClasses">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!classes.length" class="ws-state">
            <i class="fa fa-graduation-cap" aria-hidden="true"></i>
            <strong>No classes available</strong>
            <span>Create a class before assigning subjects to its options.</span>
        </div>

        <div v-else-if="!selectedClassId" class="ws-state">
            <i class="fa fa-filter" aria-hidden="true"></i>
            <strong>Select a class</strong>
            <span>Choose a class above to see the subjects assigned to its options.</span>
        </div>

        <div v-else-if="optionsLoading && optionitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading options</strong>
            <span>Fetching the options registered for {{ selectedClassName }}.</span>
        </div>

        <div v-else-if="optionsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Options could not be loaded</strong>
            <span>Try loading the options for {{ selectedClassName }} again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryOptions">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!options.length" class="ws-state">
            <i class="fa fa-sitemap" aria-hidden="true"></i>
            <strong>{{ selectedClassName }} has no options</strong>
            <span>Subjects are assigned to an option. Create an option for this class first.</span>
            <router-link
                to="/school/academic-management/options"
                class="btn ws-ghost-button"
            >
                <i class="fa fa-arrow-right" aria-hidden="true"></i>
                <span>Go to options</span>
            </router-link>
        </div>

        <div v-else-if="!selectedOptionId" class="ws-state">
            <i class="fa fa-filter" aria-hidden="true"></i>
            <strong>Select an option</strong>
            <span>Choose an option above to see the subjects assigned under it.</span>
        </div>

        <div v-else-if="subjectsLoading && schsubjectitems === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading subjects</strong>
            <span>Fetching subjects for {{ selectedClassName }} · {{ selectedOptionName }}.</span>
        </div>

        <div v-else-if="subjectsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Subjects could not be loaded</strong>
            <span>Try loading the subjects for {{ selectedOptionName }} again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retrySubjects">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!subjects.length" class="ws-state">
            <i class="fa fa-book" aria-hidden="true"></i>
            <strong>No subjects assigned for {{ selectedClassName }} · {{ selectedOptionName }}</strong>
            <span>Assign a subject to make it available for teaching, marks and reports.</span>
            <button type="button" class="btn ws-ghost-button" @click="openAssign">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Assign subject</span>
            </button>
        </div>

        <div v-else-if="!filteredSubjects.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong>No subjects match “{{ searchval.trim() }}”</strong>
            <span>Try a different subject name.</span>
            <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                Clear search
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': subjectsLoading }">
            <article
                v-for="(item, index) in filteredSubjects"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ String(index + 1).padStart(2, "0") }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ subjectName(item) }}</span>
                    <span class="ws-card__meta">
                        {{ selectedClassName }} · {{ selectedOptionName }}
                    </span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-danger-button"
                        :disabled="isSaving"
                        :aria-label="`Unassign ${subjectName(item)}`"
                        @click="unassignSubject(item)"
                    >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        <span>Unassign</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-book"
        eyebrow="NEW ASSIGNMENT"
        title="Assign a subject"
        :subtitle="drawerSubtitle"
        :saving="isSaving"
        submit-label="Assign subject"
        :submit-disabled="submitDisabled"
        form-id="subject-assign-form"
        @close="closeForm"
        @submit="assignSubject"
    >
        <fieldset class="ws-fieldset">
            <legend>Assignment details</legend>

            <div class="row">
                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="subject-class">
                            Class <span class="ws-required">*</span>
                        </label>
                        <select
                            id="subject-class"
                            v-model="form.classId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.classId }"
                            :disabled="isSaving || !classes.length"
                            @change="onFormClassChange"
                        >
                            <option :value="0">Select class</option>
                            <option v-for="item in classes" :key="item.Id" :value="item.Id">
                                {{ item.Sch_Class }}
                            </option>
                        </select>
                        <span v-if="errors.classId" class="ws-error" role="alert">
                            {{ errors.classId }}
                        </span>
                    </div>
                </div>

                <div class="col-sm-6">
                    <div class="form-group">
                        <label for="subject-option">
                            Option <span class="ws-required">*</span>
                        </label>
                        <select
                            id="subject-option"
                            v-model="form.optionId"
                            class="form-control"
                            :class="{ 'is-invalid': errors.optionId }"
                            :disabled="isSaving || !form.classId || formOptionsLoading"
                            @change="onFormOptionChange"
                        >
                            <option v-if="!form.classId" :value="0" disabled>
                                Select a class first
                            </option>
                            <option v-else-if="formOptionsLoading" :value="0" disabled>
                                Loading options…
                            </option>
                            <option v-else-if="!formOptions.length" :value="0" disabled>
                                No options for this class
                            </option>
                            <option v-else :value="0">Select option</option>
                            <option v-for="item in formOptions" :key="item.Id" :value="item.Id">
                                {{ item.ClassOption }}
                            </option>
                        </select>
                        <span v-if="errors.optionId" class="ws-error" role="alert">
                            {{ errors.optionId }}
                        </span>
                    </div>
                </div>
            </div>

            <div class="form-group">
                <label for="subject-name">
                    Subject <span class="ws-required">*</span>
                </label>
                <select
                    id="subject-name"
                    v-model="form.subjectId"
                    class="form-control"
                    :class="{ 'is-invalid': errors.subjectId }"
                    :disabled="isSaving || !form.optionId || availableBusyForForm"
                    @change="clearError('subjectId')"
                >
                    <option v-if="!form.classId" :value="0" disabled>Select a class first</option>
                    <option v-else-if="formOptionsLoading" :value="0" disabled>
                        Loading options…
                    </option>
                    <option v-else-if="!formOptions.length" :value="0" disabled>
                        No options for this class
                    </option>
                    <option v-else-if="!form.optionId" :value="0" disabled>
                        Select an option first
                    </option>
                    <option v-else-if="availableBusyForForm" :value="0" disabled>
                        Loading subjects…
                    </option>
                    <option v-else-if="!availableForForm.length" :value="0" disabled>
                        No subjects available
                    </option>
                    <option v-else :value="0">Select subject</option>
                    <option v-for="item in availableForForm" :key="item.Id" :value="item.Id">
                        {{ item.Title }}
                    </option>
                </select>
                <span v-if="errors.subjectId" class="ws-error" role="alert">
                    {{ errors.subjectId }}
                </span>
                <span v-else-if="availableFailedForForm" class="ws-error" role="alert">
                    Available subjects could not be loaded. Choose the option again to retry.
                </span>
                <span v-else class="ws-hint">
                    Only subjects not yet assigned to this class and option are listed.
                </span>
            </div>

            <div v-if="showFormOptionsNotice" class="ws-notice">
                <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                <span>
                    This class has no options yet. Create an option before assigning
                    subjects.
                    <router-link to="/school/academic-management/options">
                        Go to options
                    </router-link>
                </span>
            </div>

            <div v-else-if="showFormSubjectsNotice" class="ws-notice">
                <i class="fa fa-info-circle" aria-hidden="true"></i>
                <span>
                    Every subject is already assigned for {{ formClassName }} ·
                    {{ formOptionName }}.
                </span>
            </div>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({ classId: 0, optionId: 0, subjectId: 0 });

export default {
    name: "subject",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            classitems: null,
            optionitems: null,
            schsubjectitems: null,
            availableitems: null,
            selectedClassId: 0,
            selectedOptionId: 0,
            searchval: "",
            classesFailed: false,
            optionsFailed: false,
            subjectsFailed: false,
            availableFailed: false,
            optionsLoading: false,
            subjectsLoading: false,
            optionSeq: 0,
            subjectSeq: 0,
            formVisible: false,
            isSaving: false,
            form: EMPTY_FORM(),
            formOptions: [],
            formOptionsLoading: false,
            formOptionsFailed: false,
            formOptionSeq: 0,
            formSubjects: [],
            formSubjectsLoading: false,
            formSubjectsFailed: false,
            formSubjectSeq: 0,
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        classes() {
            return Array.isArray(this.classitems) ? this.classitems : [];
        },
        options() {
            return Array.isArray(this.optionitems) ? this.optionitems : [];
        },
        subjects() {
            return Array.isArray(this.schsubjectitems) ? this.schsubjectitems : [];
        },
        classesLoading() {
            return this.classitems === null;
        },
        selectedClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.selectedClassId)
            );
            return selected ? selected.Sch_Class : "";
        },
        selectedOptionName() {
            const selected = this.options.find(
                item => String(item.Id) === String(this.selectedOptionId)
            );
            return selected ? selected.ClassOption : "";
        },
        formClassName() {
            const selected = this.classes.find(
                item => String(item.Id) === String(this.form.classId)
            );
            return selected ? selected.Sch_Class : "";
        },
        formOptionName() {
            const selected = this.formOptions.find(
                item => String(item.Id) === String(this.form.optionId)
            );
            return selected ? selected.ClassOption : "";
        },
        formMatchesScope() {
            return (
                Boolean(this.form.optionId) &&
                String(this.form.classId) === String(this.selectedClassId) &&
                String(this.form.optionId) === String(this.selectedOptionId)
            );
        },
        availableForForm() {
            if (this.formMatchesScope) {
                return Array.isArray(this.availableitems) ? this.availableitems : [];
            }
            return this.formSubjects;
        },
        availableBusyForForm() {
            return this.formMatchesScope ? this.subjectsLoading : this.formSubjectsLoading;
        },
        availableFailedForForm() {
            return this.formMatchesScope ? this.availableFailed : this.formSubjectsFailed;
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredSubjects() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.subjects;

            return this.subjects.filter(item => {
                const haystack = [item && item.SchSubject]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        scopeLabel() {
            if (this.classesLoading) return "Loading classes…";
            if (this.classesFailed) return "Classes unavailable.";
            if (!this.classes.length) return "No classes available.";
            if (!this.selectedClassId) return "Choose a class to view its subjects.";
            if (this.optionsLoading) return `${this.selectedClassName} · loading options…`;
            if (this.optionsFailed) return `${this.selectedClassName} · options unavailable`;
            if (!this.options.length) return `${this.selectedClassName} · no options`;
            if (!this.selectedOptionId) return `${this.selectedClassName} · choose an option`;
            if (this.subjectsLoading) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · loading subjects…`;
            }
            if (this.subjectsFailed) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · subjects unavailable`;
            }
            if (this.hasSearch) {
                return `${this.selectedClassName} · ${this.selectedOptionName} · ${this.filteredSubjects.length} of ${this.subjects.length} shown`;
            }
            const count = this.subjects.length;
            return `${this.selectedClassName} · ${this.selectedOptionName} · ${count} ${count === 1 ? "subject" : "subjects"}`;
        },
        drawerSubtitle() {
            const scope = [this.formClassName, this.formOptionName]
                .filter(Boolean)
                .join(" · ");
            return scope || "Choose a class, option and subject to assign.";
        },
        showFormOptionsNotice() {
            return (
                Boolean(this.form.classId) &&
                !this.formOptionsLoading &&
                !this.formOptionsFailed &&
                !this.formOptions.length
            );
        },
        showFormSubjectsNotice() {
            return (
                Boolean(this.form.optionId) &&
                Boolean(this.formOptions.length) &&
                !this.availableBusyForForm &&
                !this.availableFailedForForm &&
                !this.availableForForm.length
            );
        },
        submitDisabled() {
            return (
                !this.formOptions.length ||
                this.availableBusyForForm ||
                !this.availableForForm.length
            );
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `subject-${index}`;
        },
        subjectName(item) {
            if (!item) return "—";
            return item.SchSubject ? String(item.SchSubject) : "—";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadClasses() {
            this.classitems = null;
            this.classesFailed = false;

            try {
                const resp = await this.$store.dispatch("getclass");
                this.classitems = Array.isArray(resp) ? resp : [];

                const exists = this.classitems.some(
                    item => String(item.Id) === String(this.selectedClassId)
                );
                if (!exists) {
                    this.selectedClassId = this.classitems.length
                        ? this.classitems[0].Id
                        : 0;
                }

                if (this.selectedClassId) {
                    await this.loadOptions();
                } else {
                    this.optionitems = null;
                    this.schsubjectitems = null;
                    this.availableitems = null;
                }
            } catch (error) {
                this.classitems = [];
                this.classesFailed = true;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load classes at the moment."),
                    ""
                );
            }
        },
        async loadOptions(options = {}) {
            const silent = Boolean(options && options.silent);
            const classId = this.selectedClassId;

            if (!classId) {
                this.optionitems = null;
                this.selectedOptionId = 0;
                this.schsubjectitems = null;
                this.availableitems = null;
                this.optionsLoading = false;
                return;
            }

            const sequence = ++this.optionSeq;
            this.optionsLoading = true;
            this.optionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbyclass", classId);
                if (sequence !== this.optionSeq) return;
                this.optionitems = Array.isArray(resp) ? resp : [];

                const exists = this.optionitems.some(
                    item => String(item.Id) === String(this.selectedOptionId)
                );
                if (!exists) {
                    this.selectedOptionId = this.optionitems.length
                        ? this.optionitems[0].Id
                        : 0;
                }
            } catch (error) {
                if (sequence !== this.optionSeq) return;
                this.optionsFailed = true;
                this.optionitems = [];
                this.selectedOptionId = 0;
                if (!silent) {
                    this.ShowAlertError(
                        this.errorMessage(error, "Unable to load options for this class."),
                        ""
                    );
                }
            } finally {
                if (sequence === this.optionSeq) this.optionsLoading = false;
            }

            if (sequence !== this.optionSeq) return;
            if (this.selectedOptionId) {
                await this.loadSubjects();
            } else {
                this.schsubjectitems = null;
                this.availableitems = null;
            }
        },
        async loadSubjects() {
            const classId = this.selectedClassId;
            const optionId = this.selectedOptionId;

            if (!classId || !optionId) {
                this.schsubjectitems = null;
                this.availableitems = null;
                this.subjectsLoading = false;
                return;
            }

            const sequence = ++this.subjectSeq;
            this.subjectsLoading = true;
            this.subjectsFailed = false;

            const payload = {
                SchsubId: 1,
                ClassId: classId,
                OptionId: optionId,
                processop: "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            try {
                const [assignedResult, availableResult] = await Promise.all([
                    this.$store
                        .dispatch("getschsubject", payload)
                        .then(data => ({ data }), error => ({ error })),
                    this.$store
                        .dispatch("getavailablesubject", payload)
                        .then(data => ({ data }), error => ({ error }))
                ]);
                if (sequence !== this.subjectSeq) return;

                if (availableResult.error) {
                    this.availableitems = [];
                    this.availableFailed = true;
                } else {
                    this.availableitems = Array.isArray(availableResult.data)
                        ? availableResult.data
                        : [];
                    this.availableFailed = false;
                }

                if (assignedResult.error) {
                    this.schsubjectitems = [];
                    this.subjectsFailed = true;
                    this.ShowAlertError(
                        this.errorMessage(
                            assignedResult.error,
                            "Unable to load assigned subjects."
                        ),
                        ""
                    );
                } else {
                    this.schsubjectitems = Array.isArray(assignedResult.data)
                        ? assignedResult.data
                        : [];
                }
            } finally {
                if (sequence === this.subjectSeq) this.subjectsLoading = false;
            }
        },
        retryClasses() {
            this.loadClasses();
        },
        retryOptions() {
            this.optionsFailed = false;
            this.optionitems = null;
            this.selectedOptionId = 0;
            this.schsubjectitems = null;
            this.availableitems = null;
            this.loadOptions();
        },
        retrySubjects() {
            this.subjectsFailed = false;
            this.schsubjectitems = null;
            this.loadSubjects();
        },
        onClassChange() {
            this.searchval = "";
            this.selectedOptionId = 0;
            this.optionitems = null;
            this.schsubjectitems = null;
            this.availableitems = null;
            this.optionsFailed = false;
            this.subjectsFailed = false;
            this.loadOptions();
        },
        onOptionChange() {
            this.searchval = "";
            this.schsubjectitems = null;
            this.subjectsFailed = false;
            this.loadSubjects();
        },
        async loadFormOptions(preferredOptionId) {
            const classId = this.form.classId;
            const sequence = ++this.formOptionSeq;

            if (!classId) {
                this.formOptions = [];
                this.form.optionId = 0;
                this.formOptionsLoading = false;
                return;
            }

            this.formOptionsLoading = true;
            this.formOptionsFailed = false;

            try {
                const resp = await this.$store.dispatch("getoptionbyclass", classId);
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = Array.isArray(resp) ? resp : [];

                const preferred =
                    preferredOptionId === null || preferredOptionId === undefined
                        ? this.form.optionId
                        : preferredOptionId;
                const exists = this.formOptions.some(
                    item => String(item.Id) === String(preferred)
                );
                this.form.optionId = exists
                    ? preferred
                    : this.formOptions.length
                    ? this.formOptions[0].Id
                    : 0;
            } catch (error) {
                if (sequence !== this.formOptionSeq) return;
                this.formOptions = [];
                this.formOptionsFailed = true;
                this.form.optionId = 0;
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load options for this class."),
                    ""
                );
            } finally {
                if (sequence === this.formOptionSeq) this.formOptionsLoading = false;
            }

            if (sequence !== this.formOptionSeq) return;
            this.loadFormSubjects();
        },
        async loadFormSubjects() {
            const classId = this.form.classId;
            const optionId = this.form.optionId;
            const sequence = ++this.formSubjectSeq;

            this.formSubjectsFailed = false;
            this.form.subjectId = 0;

            if (!classId || !optionId) {
                this.formSubjects = [];
                this.formSubjectsLoading = false;
                return;
            }

            if (this.formMatchesScope && this.availableitems !== null) {
                this.formSubjects = [];
                this.formSubjectsLoading = false;
                return;
            }

            this.formSubjectsLoading = true;

            try {
                const payload = {
                    SchsubId: 1,
                    ClassId: classId,
                    OptionId: optionId,
                    processop: "N",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };
                const resp = await this.$store.dispatch("getavailablesubject", payload);
                if (sequence !== this.formSubjectSeq) return;
                this.formSubjects = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.formSubjectSeq) return;
                this.formSubjects = [];
                this.formSubjectsFailed = true;
                this.ShowAlertError(
                    this.errorMessage(
                        error,
                        "Unable to load available subjects for this scope."
                    ),
                    ""
                );
            } finally {
                if (sequence === this.formSubjectSeq) this.formSubjectsLoading = false;
            }
        },
        onFormClassChange() {
            this.formOptions = [];
            this.form.optionId = 0;
            this.form.subjectId = 0;
            this.formSubjects = [];
            this.formSubjectsFailed = false;
            this.formSubjectsLoading = false;
            this.formSubjectSeq += 1;
            this.clearError("classId");
            this.clearError("optionId");
            this.clearError("subjectId");
            this.loadFormOptions(0);
        },
        onFormOptionChange() {
            this.form.subjectId = 0;
            this.formSubjects = [];
            this.clearError("optionId");
            this.clearError("subjectId");
            this.loadFormSubjects();
        },
        resetForm() {
            this.form = EMPTY_FORM();
            this.errors = {};
            this.formOptions = [];
            this.formOptionsLoading = false;
            this.formOptionsFailed = false;
            this.formSubjects = [];
            this.formSubjectsLoading = false;
            this.formSubjectsFailed = false;
            this.formOptionSeq += 1;
            this.formSubjectSeq += 1;
        },
        openAssign() {
            if (!this.classes.length) return;
            this.resetForm();
            this.form = {
                classId: this.selectedClassId || 0,
                optionId: this.selectedOptionId || 0,
                subjectId: 0
            };
            this.formVisible = true;
            if (this.form.classId) {
                this.loadFormOptions(this.form.optionId);
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};

            const classId = this.form.classId;
            if (
                classId === 0 ||
                classId === "0" ||
                classId === "" ||
                classId === null ||
                classId === undefined
            ) {
                errors.classId = "Choose the class for this assignment.";
            }

            const optionId = this.form.optionId;
            if (
                optionId === 0 ||
                optionId === "0" ||
                optionId === "" ||
                optionId === null ||
                optionId === undefined
            ) {
                errors.optionId = "Choose the option this subject belongs to.";
            }

            const subjectId = this.form.subjectId;
            if (
                subjectId === 0 ||
                subjectId === "0" ||
                subjectId === "" ||
                subjectId === null ||
                subjectId === undefined
            ) {
                errors.subjectId = "Choose the subject to assign.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const fieldIds = {
                    classId: "subject-class",
                    optionId: "subject-option",
                    subjectId: "subject-name"
                };
                const firstField = Object.keys(errors)[0];
                const element = document.getElementById(fieldIds[firstField]);
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async assignSubject() {
            if (this.isSaving) return;
            if (!this.formOptions.length) return;
            if (this.availableBusyForForm) return;
            if (!this.availableForForm.length) return;
            if (!this.validate()) return;

            const savedClass = this.form.classId;
            const savedOption = this.form.optionId;
            const payload = {
                SchsubId: this.form.subjectId,
                ClassId: savedClass,
                OptionId: savedOption,
                processop: "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("registerschsubject", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The subject could not be assigned.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                this.selectedClassId = savedClass;
                this.selectedOptionId = savedOption;
                this.optionitems = null;
                await this.loadOptions({ silent: true });

                if (this.optionsFailed || this.subjectsFailed) {
                    this.ShowAlertWarning(
                        "Subject assigned, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Subject assigned successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to assign this subject at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        },
        async unassignSubject(item) {
            if (!item || this.isSaving) return;

            const name = this.subjectName(item);
            const confirmed = await this.showSchooldockAlert({
                title: "Unassign subject?",
                msg: `${name} will no longer be offered for ${this.selectedClassName} · ${this.selectedOptionName}.`,
                icon: "warning",
                confirmButtonColor: "#c23b4a",
                showCancelButton: true
            });
            if (!confirmed || !confirmed.isConfirmed) return;

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const record = await this.$store.dispatch("getschsubjectbyid", item.Id);
                if (!record) throw new Error("The assigned subject could not be loaded.");

                const payload = {
                    SchsubId:
                        record.SchsubId !== undefined && record.SchsubId !== null
                            ? record.SchsubId
                            : item.Id,
                    ClassId: record.ClassId,
                    OptionId: record.OptionId,
                    processop: "D",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };
                const resp = await this.$store.dispatch("registerschsubject", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The subject could not be unassigned.",
                        ""
                    );
                    return;
                }

                await this.loadSubjects();

                if (this.subjectsFailed) {
                    this.ShowAlertWarning(
                        "Subject unassigned, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Subject unassigned successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to unassign this subject at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadClasses();
    }
};
</script>
